import { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE } from '../lib/api';

const UserAuthContext = createContext();

export function UserAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (!token) {
      setLoading(false);
      return;
    }

    fetch(`${API_BASE}/user/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(data => setUser(data))
      .catch(() => {
        localStorage.removeItem('user_token');
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/user/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message);
    localStorage.setItem('user_token', data.token);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password, stage_id, grade_id) => {
    const res = await fetch(`${API_BASE}/user/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, stage_id, grade_id })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message);
    localStorage.setItem('user_token', data.token);
    setUser(data.user);
    return data;
  };

  const googleLogin = async (id_token) => {
    const res = await fetch(`${API_BASE}/user/google-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message);
    localStorage.setItem('user_token', data.token);
    setUser(data.user);
    return data;
  };

  const appleLogin = async (identity_token, name) => {
    const res = await fetch(`${API_BASE}/user/apple-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identity_token, name })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message);
    localStorage.setItem('user_token', data.token);
    setUser(data.user);
    return data;
  };

  const updateUser = (updatedFields) => {
    setUser(prev => prev ? { ...prev, ...updatedFields } : prev);
  };

  const logout = () => {
    localStorage.removeItem('user_token');
    setUser(null);
  };

  const token = localStorage.getItem('user_token');

  return (
    <UserAuthContext.Provider value={{ user, token, loading, login, register, googleLogin, appleLogin, logout, updateUser }}>
      {children}
    </UserAuthContext.Provider>
  );
}

export function useUserAuth() {
  const context = useContext(UserAuthContext);
  if (!context) throw new Error('useUserAuth must be used within UserAuthProvider');
  return context;
}
